import { Download, Upload } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger
} from '@/components/ui/tooltip';
import { Typography } from '@/components/ui/typography';
import { useStagedActions } from './useStagedActions';

type StagedLeadingActionsProps = {
  setShowDownloadModal: (show: boolean) => void;
  onOpenUpload: () => void;
};

// AssetsControlBar's `leadingActions` slot for the Virtual Staging tab: the
// select-all checkbox + live count, and the Download/Upload icon buttons.
// Below lg these two buttons relocate into the kebab (StagedMenuItems) —
// max-lg:hidden here keeps them from doubling up (mirrors PhotosLeadingActions).
const StagedLeadingActions = ({
  setShowDownloadModal,
  onOpenUpload
}: StagedLeadingActionsProps) => {
  const {
    uploadEnabled,
    stagedCount,
    selectedCount,
    hasSelection,
    hasSelectableStaged,
    isGlobalChecked,
    isIndeterminate,
    downloadLabel,
    toggleSelectAll
  } = useStagedActions();

  return (
    <div className="flex items-center gap-3">
      <div className="flex items-center gap-2">
        <Tooltip delayDuration={250}>
          <TooltipTrigger asChild>
            {/* div wrapper: see PhotosLeadingActions — Tooltip's data-state
                would overwrite Checkbox's indeterminate styling. */}
            <div className="inline-flex">
              <Checkbox
                checked={isIndeterminate ? 'indeterminate' : isGlobalChecked}
                onCheckedChange={toggleSelectAll}
                disabled={!hasSelectableStaged}
                aria-label="Select All Staged Photos"
              />
            </div>
          </TooltipTrigger>
          <TooltipContent side="bottom">Select All</TooltipContent>
        </Tooltip>
        <div className="flex flex-col leading-5 gap-0.5">
          <Typography variant="body-semibold" className="text-foreground">
            {stagedCount} {stagedCount === 1 ? 'Photo' : 'Photos'}
          </Typography>
          {hasSelection && (
            <Typography variant="body" className="text-muted-foreground">
              {selectedCount} selected
            </Typography>
          )}
        </div>
      </div>
      <Tooltip delayDuration={250}>
        <TooltipTrigger asChild>
          <Button
            variant="outline"
            size="icon"
            onClick={() => setShowDownloadModal(true)}
            disabled={!hasSelectableStaged}
            aria-label={downloadLabel}
            className="border-input max-lg:hidden"
          >
            <Download className="w-4 h-4" />
          </Button>
        </TooltipTrigger>
        <TooltipContent side="bottom">{downloadLabel}</TooltipContent>
      </Tooltip>
      {uploadEnabled && (
        <Tooltip delayDuration={250}>
          <TooltipTrigger asChild>
            <Button
              variant="outline"
              size="icon"
              onClick={onOpenUpload}
              aria-label="Upload"
              className="border-input max-lg:hidden"
            >
              <Upload className="w-4 h-4" />
            </Button>
          </TooltipTrigger>
          <TooltipContent side="bottom">Upload Staged Photos</TooltipContent>
        </Tooltip>
      )}
    </div>
  );
};

export default StagedLeadingActions;
